import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import type { GuestDetail } from '@camp67/model';
import { GuestCardExpandable } from './GuestCardExpandable';
import { FormSection, FormSectionHeader, FormSectionContent } from '../BookingFormLayout';
import { phoneCodes } from '../../../constants/phone-codes';
import { useBrandTheme } from '../brand-theme';
import type { GuestDetailsMessages } from '../types';

interface GuestDetailsStepProps {
  guests: GuestDetail[];
  primaryContactId: string;
  primaryEmail: string;
  primaryPhone: string;
  countryCode: string;
  onGuestChange: (id: string, detail: Partial<GuestDetail>) => void;
  onPrimaryContactChange: (id: string) => void;
  onEmailChange: (email: string) => void;
  onPhoneChange: (phone: string) => void;
  onCountryCodeChange: (code: string) => void;
  emailError?: string;
  phoneError?: string;
  messages: GuestDetailsMessages;
}

/**
 * Guest details step container.
 * Displays expandable guest cards and primary contact form.
 */
export const GuestDetailsStep: React.FC<GuestDetailsStepProps> = ({
  guests,
  primaryContactId,
  primaryEmail,
  primaryPhone,
  countryCode,
  onGuestChange,
  onPrimaryContactChange,
  onEmailChange,
  onPhoneChange,
  onCountryCodeChange,
  emailError,
  phoneError,
  messages,
}) => {
  const { theme } = useBrandTheme();

  // Track which guest card is expanded
  const [expandedGuestId, setExpandedGuestId] = useState<string | null>(
    guests.length > 0 ? guests[0].id : null,
  );

  // Auto-expand first incomplete guest
  useEffect(() => {
    const firstIncomplete = guests.find(
      (g) => !g.name || g.age === 0 || g.gender === 'none',
    );
    if (firstIncomplete && expandedGuestId === null) {
      setExpandedGuestId(firstIncomplete.id);
    }
  }, [guests, expandedGuestId]);

  const handleToggleGuest = useCallback(
    (guestId: string) => {
      setExpandedGuestId((prev) => (prev === guestId ? null : guestId));
    },
    [],
  );

  // Find valid guests for primary contact selection
  const validGuests = guests.filter(
    (g) => g.name && g.age > 0 && g.gender !== 'none',
  );
  const canSelectPrimaryContact = validGuests.length > 0;

  // Auto-select first valid guest as primary contact if none selected
  useEffect(() => {
    if (validGuests.length > 0 && !primaryContactId) {
      onPrimaryContactChange(validGuests[0].id);
    }
  }, [validGuests, primaryContactId, onPrimaryContactChange]);

  // Count completed guests
  const completedCount = validGuests.length;
  const totalCount = guests.length;

  return (
    <FormSection>
      <FormSectionHeader
        title={messages.title}
        subtitle={messages.subtitle}
        action={
          <span className="text-sm font-medium text-gray-500">
            {completedCount}/{totalCount} completed
          </span>
        }
      />
      <FormSectionContent>
        <div className="space-y-6">
          {/* Guest cards section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{messages.guestsTitle}</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">{messages.guestsDisclaimer}</p>

            {/* Guest cards */}
            <div className="space-y-3">
              {guests.map((guest, index) => (
                <GuestCardExpandable
                  key={guest.id}
                  guest={guest}
                  index={index}
                  isExpanded={expandedGuestId === guest.id}
                  isPrimaryContact={primaryContactId === guest.id}
                  onToggle={() => handleToggleGuest(guest.id)}
                  onChange={onGuestChange}
                  messages={messages.guestCard}
                />
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200" />

          {/* Primary contact section */}
          <div
            className={clsx(
              'transition-opacity duration-200',
              !canSelectPrimaryContact && 'opacity-50 pointer-events-none',
            )}
          >
            <h3 className="font-semibold text-gray-900 mb-1">
              {messages.primaryContactTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{messages.primaryContactInfo}</p>

            {/* Primary contact selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {messages.selectPrimaryContact}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Click a name below to select them as the primary contact
              </p>
              <div className="flex flex-wrap gap-2">
                {validGuests.map((guest) => {
                  const isSelected = primaryContactId === guest.id;
                  return (
                    <button
                      key={guest.id}
                      type="button"
                      onClick={() => onPrimaryContactChange(guest.id)}
                      className={clsx(
                        'px-4 py-2 rounded-full text-sm font-medium',
                        'border-2 transition-all duration-150',
                        'focus:outline-none focus:ring-2 focus:ring-offset-1',
                        theme.ctaRing,
                        isSelected
                          ? `${theme.accentBorder} ${theme.accentBgLight} ${theme.accentText}`
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
                      )}
                    >
                      {isSelected && (
                        <span className="inline-flex items-center mr-1.5">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                      )}
                      {guest.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Email and Phone fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder={messages.emailPlaceholder}
                  className={clsx(
                    'w-full px-3 py-2.5 rounded-lg border',
                    'text-gray-900 placeholder-gray-400',
                    'focus:outline-none focus:ring-2 focus:border-transparent',
                    theme.ctaRing,
                    'transition-colors duration-150',
                    emailError
                      ? 'border-red-300 bg-red-50'
                      : primaryEmail
                        ? 'border-gray-200 bg-white'
                        : 'border-gray-300 bg-gray-50',
                  )}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>

              {/* Phone field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <div className="flex gap-2">
                  {/* Country code selector */}
                  <select
                    value={countryCode}
                    onChange={(e) => onCountryCodeChange(e.target.value)}
                    className={clsx(
                      'px-2 py-2.5 rounded-lg border border-gray-200',
                      'text-gray-900 bg-white',
                      'focus:outline-none focus:ring-2 focus:border-transparent',
                      theme.ctaRing,
                      'w-24 flex-shrink-0',
                    )}
                  >
                    {phoneCodes.map((code) => (
                      <option key={code.c} value={code.d}>
                        {code.d}
                      </option>
                    ))}
                  </select>

                  {/* Phone number input */}
                  <input
                    type="tel"
                    value={primaryPhone}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    placeholder={messages.phonePlaceholder}
                    className={clsx(
                      'flex-1 px-3 py-2.5 rounded-lg border',
                      'text-gray-900 placeholder-gray-400',
                      'focus:outline-none focus:ring-2 focus:border-transparent',
                      theme.ctaRing,
                      'transition-colors duration-150',
                      phoneError
                        ? 'border-red-300 bg-red-50'
                        : primaryPhone
                          ? 'border-gray-200 bg-white'
                          : 'border-gray-300 bg-gray-50',
                    )}
                  />
                </div>
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </FormSectionContent>
    </FormSection>
  );
};

export default GuestDetailsStep;
